import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import useServerPagination from '@/hooks/useServerPagination';
import { ProductResponse } from '@/providers/HttpProvider/Fulfill/Inventory/types';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { getProductId, Product, WarehouseDetailRowState } from '@/types/fulfill/inventory';
import { downloadBlobFile } from '@/utils/common';

import { InventoryTableProps } from './InventoryTable';

interface ProductsState {
  loading: boolean;
  error: string | null;
  data: ProductResponse['products'];
  metadata: {
    total_pages: number;
    total_records: number;
  };
}

interface EditProductState {
  product: Product | null;
  open: boolean;
}

interface KitCompositionState {
  product: Product | null;
  open: boolean;
}

const initialProductsState: ProductsState = {
  loading: false,
  error: null,
  data: [],
  metadata: {
    total_pages: 0,
    total_records: 0,
  },
};

const useInventory = ({ handleProductsSelected, onActiveSkuCountChange }: InventoryTableProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState<string>('');
  const searchRef = useRef<string>('');
  const [products, setProducts] = useState<ProductsState>(initialProductsState);
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedProductsMap, setSelectedProductsMap] = useState<Map<string, Product>>(new Map());
  const [editProduct, setEditProduct] = useState<EditProductState>({
    product: null,
    open: false,
  });
  const [kitComposition, setKitComposition] = useState<KitCompositionState>({
    product: null,
    open: false,
  });

  const pagination = useServerPagination();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const handleProductsSelectedRef = useRef(handleProductsSelected);
  const [expandedRows, setExpandedRows] = useState<Record<string, WarehouseDetailRowState>>({});

  useEffect(() => {
    handleProductsSelectedRef.current = handleProductsSelected;
  }, [handleProductsSelected]);

  const toggleDrawer = useCallback(() => {
    setDrawerOpen(!drawerOpen);
  }, [drawerOpen]);

  const getProducts = useCallback(async () => {
    const http = initializeHttpProvider();
    const currentSearch = searchRef.current;
    try {
      setProducts((prev) => ({ ...prev, loading: true }));
      const response = await http.inventory.products({
        page: (pagination.filters.page ?? 0) + 1,
        page_size: pagination.filters.page_size,
        onlyActive: true,
        warehouse_detail: true,
        ...(currentSearch.length > 0 && { search: currentSearch }),
      });

      setProducts((prev) => ({
        ...prev,
        loading: false,
        data: response.products,
        metadata: {
          total_pages: response.metadata.total_pages,
          total_records: response.metadata.total_records,
        },
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      setProducts((prev) => ({ ...prev, loading: false, error: errorMessage }));
    }
  }, [pagination.filters]);

  const getReport = useCallback(async () => {
    const http = initializeHttpProvider();
    return toast.promise(
      async () => {
        const response = await http.inventory.reportProducts({ onlyActive: true });
        downloadBlobFile(response.blob, response.filename ?? 'inventory-report.xlsx');
      },
      {
        pending: 'Descargando reporte...',
        success: 'Reporte descargado',
        error: 'No se pudo descargar el reporte',
      },
    );
  }, []);

  const clearSelection = useCallback(() => {
    setSelected([]);
    setSelectedProductsMap(new Map());
  }, []);

  const handleSelect = useCallback(
    (newSelected: string[]) => {
      // Crear una copia del mapa de productos seleccionados
      const updatedMap = new Map(selectedProductsMap);
      const newSelectedSet = new Set(newSelected);

      // Identificar productos de la página actual que fueron agregados
      const newlySelected = newSelected.filter((id) => !selectedProductsMap.has(id));
      newlySelected.forEach((id) => {
        const product = products.data.find((p) => getProductId(p) === id);
        if (product) {
          updatedMap.set(id, product);
        }
      });

      // Identificar productos de la página actual que fueron removidos
      const currentPageIds = new Set(products.data.map((p) => getProductId(p)));
      const deselectedFromCurrentPage = Array.from(selectedProductsMap.keys()).filter(
        (id) => currentPageIds.has(id) && !newSelectedSet.has(id),
      );
      deselectedFromCurrentPage.forEach((id) => {
        updatedMap.delete(id);
      });

      // Actualizar ambos estados de forma síncrona
      const allSelectedIds = Array.from(updatedMap.keys());
      setSelectedProductsMap(updatedMap);
      setSelected(allSelectedIds);
    },
    [products.data, selectedProductsMap],
  );

  const handleEditProduct = useCallback((product: Product) => {
    setEditProduct({ product, open: true });
  }, []);

  const closeEditProduct = useCallback(() => {
    setEditProduct({ product: null, open: false });
  }, []);

  const handleViewKitComposition = useCallback((product: Product) => {
    setKitComposition({ product, open: true });
  }, []);

  const closeKitComposition = useCallback(() => {
    setKitComposition((prev) => ({ ...prev, open: false }));
  }, []);

  const toggleWarehouseDetail = useCallback((product: Product) => {
    const id = getProductId(product);
    setExpandedRows((prev) => {
      if (!prev[id]) return { ...prev, [id]: 'open' };
      if (prev[id] === 'open') return { ...prev, [id]: 'closing' };
      return prev;
    });
  }, []);

  const finalizeWarehouseCollapse = useCallback((product: Product) => {
    const id = getProductId(product);
    setExpandedRows((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  useEffect(() => {
    void getProducts();
  }, []);

  useEffect(() => {
    void getProducts();
  }, [pagination.filters]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const resetPageAndFetch = useCallback(() => {
    pagination.onChangeFilter({ page: 0 });
    if ((pagination.filters.page ?? 0) === 0) {
      void getProducts();
    }
  }, [getProducts, pagination]);

  const onChgSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      searchRef.current = value;
      setSearch(value);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (value.length === 0) {
        resetPageAndFetch();
      } else if (value.length >= 3) {
        debounceTimerRef.current = setTimeout(resetPageAndFetch, 500);
      }
    },
    [resetPageAndFetch],
  );

  const onSearch = useCallback(() => {
    void getProducts();
    pagination.onChangeFilter({ page: 0 });
  }, [getProducts, pagination]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  useEffect(() => {
    const allSelectedProducts = Array.from(selectedProductsMap.values());
    handleProductsSelectedRef.current(allSelectedProducts);
  }, [selectedProductsMap]);

  useEffect(() => {
    if (onActiveSkuCountChange !== undefined && !search) {
      onActiveSkuCountChange(products.metadata.total_records);
    }
  }, [products.metadata.total_records, onActiveSkuCountChange, search]);

  return {
    getProducts,
    products,
    search,
    drawerOpen,
    toggleDrawer,
    handleSelect,
    selected,
    handleEditProduct,
    closeEditProduct,
    editProduct,
    handleViewKitComposition,
    closeKitComposition,
    kitComposition,
    pagination,
    onChgSearch,
    handleKeyPress,
    onSearch,
    getReport,
    clearSelection,
    expandedRows,
    toggleWarehouseDetail,
    finalizeWarehouseCollapse,
  };
};

export default useInventory;
